/**
 * DriveAttachmentChip — render unificado de um anexo "referência viva" do Drive
 * (E14.2.5, FR-E14-12). Usado em chat (bolha), feed (post) e tarefa (detalhe).
 *
 * Dado o `documentId` + nome (+ extensão/tamanho denormalizados pelo backend),
 * oferece abrir/baixar/editar via os endpoints atuais de documento (escopo
 * company, como o resto do módulo) — sem duplicar bytes. Office abre no editor
 * OnlyOffice (E14.2.3); os demais abrem no preview.
 */
import { useState } from "react";
import { Download, Eye, FileText, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { documentApi } from "@/services/documentApi";
import { triggerBrowserDownload } from "@/lib/browser-download";
import { isOnlyOfficeEditable } from "@/lib/onlyoffice-iframe-wrapper";
import { DocumentPreviewDialog } from "@/components/documents/DocumentPreviewDialog";
import { DocumentEditorDialog } from "@/components/drive/DocumentEditorDialog";

export interface DriveAttachmentChipProps {
  documentId: string;
  name: string;
  sizeBytes?: number | null;
  fileExtension?: string | null;
  className?: string;
}

function formatBytes(bytes?: number | null): string {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

export function DriveAttachmentChip({
  documentId,
  name,
  sizeBytes,
  fileExtension,
  className,
}: DriveAttachmentChipProps) {
  const [preview, setPreview] = useState(false);
  const [editing, setEditing] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const editable = isOnlyOfficeEditable({
    file_extension: fileExtension ?? undefined,
    name,
  });
  const sizeLabel = formatBytes(sizeBytes);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const { download_url } = await documentApi.getDownloadUrl(documentId, "attachment");
      triggerBrowserDownload(download_url);
    } catch {
      toast.error("Erro ao baixar o documento");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      data-testid="drive-attachment-chip"
      className={`flex items-center gap-2 rounded-md border bg-muted/30 px-2 py-1.5 ${
        className ?? ""
      }`}
    >
      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{name}</p>
        <p className="text-xs text-muted-foreground">Drive{sizeLabel ? ` · ${sizeLabel}` : ""}</p>
      </div>
      <div className="flex shrink-0 items-center gap-0.5">
        {editable ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            onClick={() => setEditing(true)}
            aria-label="Abrir no editor"
            title="Abrir no editor"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            onClick={() => setPreview(true)}
            aria-label="Visualizar"
            title="Visualizar"
          >
            <Eye className="h-3.5 w-3.5" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2"
          onClick={handleDownload}
          disabled={downloading}
          aria-label="Baixar"
          title="Baixar"
        >
          {downloading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Download className="h-3.5 w-3.5" />
          )}
        </Button>
      </div>

      {preview && (
        <DocumentPreviewDialog documentId={documentId} open={preview} onOpenChange={setPreview} />
      )}
      {editing && (
        <DocumentEditorDialog
          documentId={documentId}
          open={editing}
          onOpenChange={setEditing}
          documentName={name}
        />
      )}
    </div>
  );
}
